import React, { useState } from 'react';

import useSWR from 'swr';
import { Protect } from '@clerk/clerk-react';
import Tippy from '@tippyjs/react';

import AuthorizationTooltip from '@components/AuthorizationTooltip';
import AllChannelsButton from '../../components/AllChannelsButton';
import ImageInput from '@components/ImageInput';
import { useCheckPermission } from '@services/auth/hooks';
import { useShop } from '@services/ShopProvider';
import { getOrderOptions, getChannel, updateChannel } from '@services/channels';
import { ReturnAndRefundPolicy, DeliveryPolicy } from './config';

import { Button, Card, Icon, Page, Input, InputGroup, Textarea, CheckboxInput } from '@nearst/ui';

import defaultHeader from '../../../../assets/default-header.jpg';

import styles from './Storefront.module.scss';

const Storefront = () => {
	const { shop, updateShop } = useShop();
	const authorized = useCheckPermission('org:channels:manage');
	const { data: orderOptions } = useSWR(`order-options-${shop.id}`, () => getOrderOptions(shop.id));
	const { data: storefrontChannel } = useSWR(['channel', shop.id, 'storefront'], () => getChannel('storefront', shop.id));
	const [shopBio, setShopBio] = useState(shop.bio || '');
	const [shopWebsite, setShopWebsite] = useState(shop.website || '');
	const [shopFacebook, setShopFacebook] = useState(shop.facebook || '');
	const [shopMessenger, setShopMessenger] = useState(shop.facebookMessenger || null);
	const [shopInstagram, setShopInstagram] = useState(shop.instagram || '');
	const [shopTwitter, setShopTwitter] = useState(shop.twitter || '');

	const showClickAndCollectMessage = orderOptions?.productReservationEnabled || orderOptions?.localCheckoutEnabled;
	const defaultReturnAndRefundPolicy = ReturnAndRefundPolicy(showClickAndCollectMessage);
	const defaultDeliveryPolicy = DeliveryPolicy(showClickAndCollectMessage, orderOptions);

	const savedReturnAndRefundPolicy = storefrontChannel?.config?.returnAndRefundPolicy ?? (shop.returnAndRefundPolicy || '');
	const savedDeliveryPolicy = storefrontChannel?.config?.deliveryPolicy ?? '';

	return (
		<>
			<Page.Header>
				<AllChannelsButton />
				<Page.HeaderCta>
					<div>
						<h1>NearSt storefront</h1>
						<p>Edit the appearance of your NearSt storefront.</p>
					</div>
					<Button primary href={`https://near.st/shop/${shop.slug || shop.legacyId}`} target="_blank">
						View your storefront
					</Button>
				</Page.HeaderCta>
			</Page.Header>
			<Page.Section className={styles.profile}>
				<Card title="Header image" description="Gives shoppers an impression of your shop">
					<ImageInput
						displayName="Header image"
						field="headerImageUrl"
						placeholder={defaultHeader}
						containerId="header-image"
						imageClass={styles.profileHeaderPicture}
						authorized={authorized}
					/>
				</Card>

				<Card title="Bio" description="This bio will appear on your NearSt storefront to give it an extra personal touch.">
					<Textarea
						className={styles.profileTextArea}
						placeholder="Enter a short bio"
						defaultValue={shopBio}
						onBlur={async (e) => {
							setShopBio(e.target.value);
							if (shop.bio !== e.target.value) {
								await updateShop(shop.id, { bio: e.target.value });
							}
						}}
						disabled={!authorized}
					/>
				</Card>

				<Card title="Social media links" description="Here you can add any links you want to show up on your storefront.">
					<AuthorizationTooltip authorized={authorized} tooltipContent="You don't have access update this link">
						<InputGroup label="Website" htmlFor="website">
							<Input
								className={styles.profileInputs}
								name="website"
								defaultValue={shopWebsite}
								placeholder="https://example.com"
								disabled={!authorized}
								onBlur={async (e) => {
									setShopWebsite(e.target.value);
									if (shopWebsite !== e.target.value) {
										await updateShop(shop.id, { website: e.target.value });
									}
								}}
							/>
						</InputGroup>
					</AuthorizationTooltip>
					<InputGroup label="Facebook" htmlFor="facebook">
						<div className={styles.facebookInputs}>
							<AuthorizationTooltip authorized={authorized} tooltipContent="You don't have access update this link">
								<Input
									className={styles.profileInputs}
									name="facebook"
									disabled={!authorized}
									placeholder="https://facebook.com/example"
									defaultValue={shopFacebook}
									onBlur={async (e) => {
										setShopFacebook(e.target.value);
										if (shopFacebook !== e.target.value) {
											await updateShop(shop.id, { facebook: e.target.value });
										}
									}}
								/>
							</AuthorizationTooltip>
							{shop.facebook ? (
								<Protect permission="org:channels:manage">
									<div className={styles.checkboxContainer}>
										<CheckboxInput
											label={'Enable Facebook Messenger'}
											defaultChecked={!!shopMessenger}
											onChange={async (e) => {
												const messengerUrl = `https://m.me/${shop.facebook.split('/').pop()}`;
												if (e.target.checked) {
													setShopMessenger(messengerUrl);
													await updateShop(shop.id, { facebookMessenger: messengerUrl });
												}
												if (!e.target.checked) {
													setShopMessenger(null);
													await updateShop(shop.id, { facebookMessenger: null });
												}
											}}
										/>
										<div className={styles.tippyIcon}>
											<Tippy
												interactive
												content={
													<div>
														The Facebook Messenger integration makes it easier for shoppers to reach you. Enter
														a Facebook link to get started.{' '}
														<a
															target="_blank"
															rel="noopener noreferrer"
															href="https://support.near.st/en/articles/6682418-how-do-i-add-facebook-messenger-to-my-nearst-shop-page"
														>
															Learn more &rarr;
														</a>
													</div>
												}
											>
												<span>
													<Icon>info</Icon>
												</span>
											</Tippy>
										</div>
									</div>
								</Protect>
							) : null}
						</div>
					</InputGroup>
					<AuthorizationTooltip authorized={authorized} tooltipContent="You don't have access update this link">
						<InputGroup label="Instagram" htmlFor="instagram">
							<Input
								className={styles.profileInputs}
								name="instagram"
								defaultValue={shopInstagram}
								placeholder="https://instagram.com/example"
								disabled={!authorized}
								onBlur={async (e) => {
									setShopInstagram(e.target.value);
									if (shopInstagram !== e.target.value) {
										await updateShop(shop.id, { instagram: e.target.value });
									}
								}}
							/>
						</InputGroup>
					</AuthorizationTooltip>
					<AuthorizationTooltip authorized={authorized} tooltipContent="You don't have access update this link">
						<InputGroup label="Twitter" htmlFor="twitter">
							<Input
								className={styles.profileInputs}
								name="twitter"
								defaultValue={shopTwitter}
								placeholder="https://twitter.com/example"
								disabled={!authorized}
								onBlur={async (e) => {
									setShopTwitter(e.target.value);
									if (shopTwitter !== e.target.value) {
										await updateShop(shop.id, { twitter: e.target.value });
									}
								}}
							/>
						</InputGroup>
					</AuthorizationTooltip>
				</Card>
				<Card title="Return and refund policy" description="The policy is linked to your NearSt storefront.">
					<Textarea
						key={storefrontChannel !== undefined && orderOptions !== undefined ? 'loaded' : 'loading'}
						className={styles.profileTextArea}
						defaultValue={savedReturnAndRefundPolicy || defaultReturnAndRefundPolicy}
						onBlur={async (e) => {
							const newValue = e.target.value;
							if (newValue !== (savedReturnAndRefundPolicy || defaultReturnAndRefundPolicy)) {
								await updateChannel(shop.id, 'storefront', { returnAndRefundPolicy: newValue });
							}
						}}
						disabled={!authorized}
					/>
				</Card>
				<Card title="Delivery policy" description="The policy is linked to your NearSt storefront.">
					<Textarea
						key={storefrontChannel !== undefined && orderOptions !== undefined ? 'loaded' : 'loading'}
						className={styles.profileTextArea}
						defaultValue={savedDeliveryPolicy || defaultDeliveryPolicy}
						onBlur={async (e) => {
							const newValue = e.target.value;
							if (newValue !== (savedDeliveryPolicy || defaultDeliveryPolicy)) {
								await updateChannel(shop.id, 'storefront', { deliveryPolicy: newValue });
							}
						}}
						disabled={!authorized}
					/>
				</Card>
			</Page.Section>
		</>
	);
};

export default Storefront;
